/**
 * The customer's bill as a PDF, and the date to pay it by (the owner, 2026-10-21).
 *   · useBillPdf    — the bill-pdf function makes the PDF from the ERP's own figures; "open" shows it, "send" puts it on WhatsApp as a document (the customer must have written in the last 24 hours).
 *   · DueDateEditor — the date a bill is to be paid by: the usual one (Automation › Reminders › Rules) or the one the office moved for this customer, with the reason. Used on the bill
 *                     and on the reminders page, so there is one place the date is read from: api.wa_due_of.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { rpc, supabase } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { Button, plainError } from '../../ui/primitives';
import { toast } from '../../ui/toast';

export interface DueInfo { month: string; dueBy: string; usualDueBy: string; extended: boolean; reason: string | null; setAt: string | null; setBy: string | null; latePct: number; lateFrom: string }

export const ddMonthYear = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });

export function useDue(customerId: string, month: string | null) {
  const { can } = useSession();
  return useQuery({ queryKey: ['wa-due', customerId, month], enabled: !!month && can('customer.payment.view'), staleTime: 30_000,
    queryFn: () => rpc<DueInfo>('wa_due_of', { p_customer_id: customerId, p_month: month }) });
}

export function useBillPdf(customerId: string, month: string | null, label: string) {
  const makePdf = async () => {
    const { data, error } = await supabase.functions.invoke('bill-pdf', { body: { customerId, month } });
    let d = data as { ok?: boolean; held?: boolean; reason?: string; path?: string; name?: string } | null;
    if (error && !d) { try { d = await (error as { context?: Response }).context?.clone().json(); } catch { /* no body */ } }
    if (d?.held) throw new Error(String(d.reason ?? 'Held for the office.'));
    if (error || !d?.ok) throw new Error(String(d?.reason ?? error?.message ?? 'The bill could not be made.'));
    return d as { path: string; name: string };
  };
  const open = useMutation({
    mutationFn: async () => {
      const file = await makePdf();
      const url = await supabase.storage.from('wa-media').createSignedUrl(file.path, 300);
      if (url.error || !url.data) throw new Error('The bill could not be opened.');
      window.open(url.data.signedUrl, '_blank', 'noopener');
    },
  });
  const send = useMutation({
    mutationFn: async () => { const file = await makePdf(); return rpc<{ ok: boolean }>('wa_send_bill', { p_customer_id: customerId, p_path: file.path, p_name: file.name, p_caption: `Aapka ${label} ka DESIGO® bill` }); },
    onSuccess: () => toast('The bill PDF is queued: it goes to the customer on WhatsApp in a few seconds.', { tone: 'ok' }),
  });
  const error = (open.error ?? send.error) as Error | null;
  return { open, send, busy: open.isPending || send.isPending, error: error ? plainError(error.message) : null };
}

export function DueDateEditor({ customerId, month }: { customerId: string; month: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const due = useDue(customerId, month);
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: (v: { dueOn: string | null; reason: string | null }) => rpc<DueInfo>('wa_set_due_override', { p_customer_id: customerId, p_month: month, p_due_on: v.dueOn, p_reason: v.reason }),
    onSuccess: () => {
      toast('The date is saved: the bill, the PDF and the reminders now say it.', { tone: 'ok' });
      setEditing(false); setReason('');
      for (const k of ['wa-due', 'wa-due-overrides', 'payment-reminders', 'wa-reminder-log']) void qc.invalidateQueries({ queryKey: [k] });
    },
    onError: (e) => toast(plainError((e as Error).message), { tone: 'bad' }),
  });
  const d = due.data;
  if (!d) return null;
  const first = new Date(`${d.usualDueBy}T00:00:00Z`);
  const minDate = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1)).toISOString().slice(0, 10);
  return (
    <div className="flex flex-wrap items-center gap-2 text-small print:hidden">
      <CalendarClock size={14} aria-hidden className="text-ink-muted" />
      <span>Pay by <b>{ddMonthYear(d.dueBy)}</b>{d.latePct > 0 ? <span className="text-ink-muted"> · {d.latePct}% late charge from {ddMonthYear(d.lateFrom)}</span> : null}</span>
      {d.extended ? <span className="rounded bg-queued-ground px-1.5 text-xs text-queued" title={`${d.reason ?? ''}${d.setBy ? ` · ${d.setBy}` : ''}`}>moved from {ddMonthYear(d.usualDueBy)}{d.reason ? ` · ${d.reason}` : ''}</span> : null}
      {can('service.case.manage') && !editing ? <Button size="dense" variant="ghost" onClick={() => { setDate(d.dueBy); setEditing(true); }}>Change date</Button> : null}
      {editing ? (
        <form className="flex w-full flex-wrap items-end gap-2 rounded-[var(--radius-control)] bg-brand-soft p-2"
          onSubmit={(e) => { e.preventDefault(); if (date && reason.trim().length >= 3) save.mutate({ dueOn: date, reason: reason.trim() }); }}>
          <label className="flex flex-col text-xs">New date to pay by
            <input type="date" className="rounded border border-line bg-surface px-2 py-1 text-small" value={date} min={minDate} onChange={(e) => setDate(e.target.value)} /></label>
          <label className="flex flex-col text-xs">Why
            <input className="w-64 rounded border border-line bg-surface px-2 py-1 text-small" placeholder="jaise: customer ne 10 tarikh bola" value={reason} onChange={(e) => setReason(e.target.value)} /></label>
          <Button size="dense" variant="primary" type="submit" disabled={!date || reason.trim().length < 3 || save.isPending}>Save</Button>
          {d.extended ? <Button size="dense" variant="ghost" type="button" disabled={save.isPending} onClick={() => save.mutate({ dueOn: null, reason: null })}>Back to the usual date</Button> : null}
          <Button size="dense" variant="ghost" type="button" onClick={() => setEditing(false)}>Cancel</Button>
          <span className="text-xs text-ink-muted">The late charge counts from the day after this date.</span>
        </form>) : null}
    </div>
  );
}
